/**
 * Per-topic layout accents for `/legal/:slug` rich hubs — page wash, tab style, SectionHead motifs.
 */

import { resolveCanonicalTopicSlug } from "../utils/topicSlugAliases";

const DEFAULT_LEGAL_VISUAL = {
  id: "lex-default",
  pageBg: "bg-[linear-gradient(180deg,#f8fafc_0%,#f1f5f9_50%,#f8fafc_100%)]",
  ambientClass:
    "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_80%_50%_at_100%_-10%,rgba(30,58,138,0.06),transparent_50%),radial-gradient(ellipse_60%_40%_at_0%_100%,rgba(15,23,42,0.04),transparent_45%)]",
  stickyTabVariant: "docked",
  sectionAccent: "bar",
  /** Optional extra Tailwind gradient classes for `LegalTopicIntroHero` */
  introGradientTailwind: "from-slate-50/75 via-white to-slate-100/55",
};

/** @typedef {typeof DEFAULT_LEGAL_VISUAL & { slug?: string }} LegalHubVisual */

/** @param {string} slug */
export function getLegalHubVisuals(slug) {
  const canonical = resolveCanonicalTopicSlug("legal", slug || "") || slug || "";
  /** @type {Record<string, Partial<LegalHubVisual>>} */
  const map = {
    "land-property-law": {
      id: "lex-land",
      pageBg:
        "bg-[linear-gradient(165deg,#f6f9ff_0%,#eef2ff_42%,#f8fafc_100%)]",
      ambientClass:
        "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_70%_45%_at_100%_20%,rgba(30,58,138,0.09),transparent_55%),radial-gradient(ellipse_50%_35%_at_0%_90%,rgba(59,130,246,0.05),transparent_50%)]",
      stickyTabVariant: "docked",
      sectionAccent: "bar",
      introGradientTailwind: "from-indigo-50/70 via-white to-slate-50",
    },
    "marriage-family-law": {
      id: "lex-family",
      pageBg:
        "bg-[linear-gradient(180deg,#faf7f6_0%,#f2e8e4_52%,#faf8f6_100%)]",
      ambientClass:
        "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_65%_40%_at_90%_-5%,rgba(127,94,99,0.12),transparent_55%),radial-gradient(ellipse_55%_35%_at_0%_100%,rgba(180,138,134,0.07),transparent_50%)]",
      stickyTabVariant: "docked-soft",
      sectionAccent: "warm-column",
      introGradientTailwind: "from-stone-50/85 via-[#fdfaf9] to-rose-50/35",
    },
    "business-corporate-law": {
      id: "lex-corp",
      pageBg:
        "bg-[linear-gradient(145deg,#f4f9f9_0%,#eaf3f4_45%,#f6faf9_100%)]",
      ambientClass:
        "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_70%_45%_at_95%_0%,rgba(70,101,116,0.11),transparent_58%)]",
      stickyTabVariant: "docked",
      sectionAccent: "corporate-double",
      introGradientTailwind: "from-teal-50/40 via-white to-slate-50/95",
    },
    "criminal-law": {
      id: "lex-criminal",
      pageBg: "bg-[linear-gradient(180deg,#eff8fc_0%,#e2f4f5_48%,#f0fafb_100%)]",
      ambientClass:
        "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_65%_40%_at_85%_-8%,rgba(22,78,99,0.14),transparent_55%)]",
      stickyTabVariant: "docked",
      sectionAccent: "bar-bold",
      introGradientTailwind: "from-slate-50 via-white to-cyan-50/30",
    },
    "employment-labour-law": {
      id: "lex-labour",
      pageBg: "bg-[linear-gradient(180deg,#eefcf9_0%,#dff7f4_50%,#f0fdfb_100%)]",
      ambientClass:
        "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_60%_38%_at_100%_0%,rgba(15,118,110,0.12),transparent_58%)]",
      stickyTabVariant: "docked-soft",
      sectionAccent: "bar",
      introGradientTailwind: "from-emerald-50/50 via-white to-slate-50",
    },
    "immigration-travel-law": {
      id: "lex-travel",
      pageBg:
        "bg-[linear-gradient(135deg,#f5f7ff_0%,#eaeefe_42%,#f8f9fc_100%)]",
      ambientClass:
        "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_58%_40%_at_0%_0%,rgba(67,56,202,0.11),transparent_55%),radial-gradient(ellipse_50%_30%_at_100%_100%,rgba(99,102,241,0.06),transparent_50%)]",
      stickyTabVariant: "docked",
      sectionAccent: "corporate-double",
      introGradientTailwind: "from-indigo-50/55 via-white to-slate-50/90",
    },
    "estate-planning-inheritance": {
      id: "lex-estate",
      pageBg:
        "bg-[linear-gradient(180deg,#fcf8f4_0%,#f4ebe2_52%,#fcfaf7_100%)]",
      ambientClass:
        "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_65%_42%_at_92%_-5%,rgba(146,64,14,0.11),transparent_58%)]",
      stickyTabVariant: "docked-soft",
      sectionAccent: "heritage",
      introGradientTailwind: "from-amber-50/55 via-white to-stone-50/90",
    },
    "debt-financial-legal": {
      id: "lex-debt",
      pageBg:
        "bg-[linear-gradient(180deg,#fffbf5_0%,#fdebd5_42%,#fffaf6_100%)]",
      ambientClass:
        "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_62%_40%_at_100%_5%,rgba(180,83,9,0.12),transparent_58%)]",
      stickyTabVariant: "docked",
      sectionAccent: "alert-stripe",
      introGradientTailwind: "from-amber-50/70 via-white to-orange-50/25",
    },
    "personal-injury-claims": {
      id: "lex-injury",
      pageBg: "bg-[linear-gradient(180deg,#fff5f5_0%,#fee4e5_46%,#fff7f7_100%)]",
      ambientClass:
        "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(ellipse_60%_40%_at_0%_-5%,rgba(185,28,28,0.1),transparent_58%)]",
      stickyTabVariant: "docked-soft",
      sectionAccent: "urgent-dot",
      introGradientTailwind: "from-red-50/45 via-white to-rose-50/40",
    },
    "contract-law": {
      id: "lex-contract",
      pageBg:
        "bg-[linear-gradient(180deg,#f4f7fd_0%,#eaf0fb_52%,#f6f9fc_100%)]",
    },
  };

  const extra = map[canonical] ?? {};
  return { ...DEFAULT_LEGAL_VISUAL, ...extra, slug: canonical };
}
